import React, { useState, useEffect, useRef } from 'react'
import { api } from '../../lib/api'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Loader2, Search, UserPlus } from 'lucide-react'

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 300

/**
 * Search for people to add to a group.
 *
 * Two scopes, matching the backend:
 *  - 'followers' (default): users who follow this parish, matched on a name or
 *    email substring.
 *  - 'all': every app user, matched on a name substring or an *exact* email —
 *    the override for adding someone who doesn't follow the parish yet.
 *
 * Results already in the group are filtered out client-side; the endpoint does
 * not exclude them.
 */
export default function MemberSearch({ parishId, existingUserIds = [], onAdd, addingUserId = null }) {
  const [query, setQuery] = useState('')
  const [scope, setScope] = useState('followers')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState(null)
  const [searched, setSearched] = useState(false)
  const requestIdRef = useRef(0)

  useEffect(() => {
    const trimmed = query.trim()
    if (!parishId || trimmed.length < MIN_QUERY_LENGTH) {
      setResults([])
      setSearched(false)
      setError(null)
      return undefined
    }

    const requestId = ++requestIdRef.current
    const timer = setTimeout(async () => {
      try {
        setSearching(true)
        setError(null)
        const users = await api.searchGroupMemberCandidates(parishId, { q: trimmed, scope })
        // Ignore responses from searches the user has already typed past
        if (requestId !== requestIdRef.current) return
        setResults(users || [])
        setSearched(true)
      } catch (err) {
        if (requestId !== requestIdRef.current) return
        console.error('Member search failed:', err)
        setError(err.message || 'Search failed')
        setResults([])
      } finally {
        if (requestId === requestIdRef.current) setSearching(false)
      }
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [query, scope, parishId])

  const visibleResults = results.filter((u) => !existingUserIds.includes(u.id))

  const scopeButtonClass = (value) =>
    `px-3 py-1 text-xs font-medium rounded transition-colors ${
      scope === value
        ? 'bg-gold text-black'
        : 'bg-white text-charcoal-muted border border-surface-border hover:text-charcoal'
    }`

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="w-4 h-4 text-charcoal-faint absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={scope === 'all' ? 'Name, or exact email address' : 'Search parish followers'}
          className="pl-9 bg-white border-surface-border text-charcoal"
        />
      </div>

      <div className="flex items-center gap-2">
        <button type="button" className={scopeButtonClass('followers')} onClick={() => setScope('followers')}>
          Parish followers
        </button>
        <button type="button" className={scopeButtonClass('all')} onClick={() => setScope('all')}>
          All app users
        </button>
      </div>

      <p className="text-xs text-charcoal-muted">
        {scope === 'all'
          ? 'Searches everyone on the app by name, or by exact email address — use this to add someone who does not follow your parish yet.'
          : 'Searches people who follow your parish by name or email.'}
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {searching && (
        <div className="flex items-center gap-2 text-sm text-charcoal-muted">
          <Loader2 className="w-4 h-4 text-gold animate-spin" />
          Searching...
        </div>
      )}

      {!searching && searched && visibleResults.length === 0 && (
        <p className="text-sm text-charcoal-muted">
          No matches.
          {scope === 'followers' && ' Try "All app users" to search beyond your parish followers.'}
        </p>
      )}

      {!searching && visibleResults.length > 0 && (
        <div className="border border-surface-border rounded divide-y divide-surface-border">
          {visibleResults.map((candidate) => (
            <div key={candidate.id} className="flex items-center justify-between gap-2 p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-charcoal truncate">
                  {candidate.name || candidate.email}
                </p>
                {candidate.name && (
                  <p className="text-xs text-charcoal-muted truncate">{candidate.email}</p>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={addingUserId === candidate.id}
                onClick={() => onAdd(candidate)}
                className="flex-shrink-0"
              >
                {addingUserId === candidate.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-1" />
                    Add
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
